import { config } from '../config';

/**
 * EntraID OAuth2 Authorization Code flow client for the Admin Console (BFF pattern).
 *
 * The SPA initiates the OAuth2 redirect to EntraID's authorize endpoint,
 * but the code exchange happens server-side via the auth-token Lambda.
 * This keeps the client_secret off the frontend entirely.
 *
 * Tokens stored in memory only (never localStorage).
 * Refresh token stored server-side in an HttpOnly cookie (managed by backend).
 * Extracts user role from ID token claims for role-based access control.
 */

interface BffTokenResponse {
  readonly idToken: string;
  readonly accessToken: string;
  readonly expiresIn: number;
}

interface BffLogoutResponse {
  readonly logoutUrl: string;
}

interface TokenStore {
  accessToken: string | null;
  idToken: string | null;
  expiresAt: number | null;
}

/** Decoded JWT payload shape (relevant claims only). */
export interface IdTokenClaims {
  readonly sub: string;
  readonly oid: string;
  readonly preferred_username: string;
  readonly name: string;
  readonly roles?: readonly string[];
  readonly groups?: readonly string[];
  readonly exp: number;
}

const STATE_KEY = 'ddns_admin_auth_state';
const VERIFIER_KEY = 'ddns_admin_code_verifier';
const SESSION_ACTIVITY_KEY = 'ddns_admin_last_activity';

/** In-memory token store — never persisted to localStorage */
const tokens: TokenStore = {
  accessToken: null,
  idToken: null,
  expiresAt: null,
};

/** Cached decoded claims from the ID token */
let cachedClaims: IdTokenClaims | null = null;

/**
 * Constructs the EntraID OAuth2 authorize URL with PKCE challenge.
 * The SPA still initiates the redirect — only the code exchange moves server-side.
 */
function getAuthorizeUrl(state: string, codeChallenge: string): string {
  const params = new URLSearchParams({
    client_id: config.entraIdClientId,
    response_type: 'code',
    redirect_uri: config.entraIdRedirectUri,
    scope: config.entraIdScopes,
    response_mode: 'query',
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });

  return `https://login.microsoftonline.com/${config.entraIdTenantId}/oauth2/v2.0/authorize?${params.toString()}`;
}

/**
 * Generates a cryptographically random state value for CSRF protection.
 */
function generateState(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Generates a PKCE code verifier (43-128 chars, unreserved characters).
 */
function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * Computes the PKCE code challenge (SHA-256 of verifier, base64url-encoded).
 */
async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

/**
 * Base64url-encodes a byte array (no padding, URL-safe).
 */
function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Decodes a JWT payload without validation (validation is done server-side).
 * Used to extract role claims for client-side UI routing.
 */
function decodeJwtPayload(token: string): IdTokenClaims | null {
  try {
    const parts = token.split('.');
    const payload = parts[1];
    if (!payload) return null;
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded) as IdTokenClaims;
  } catch {
    return null;
  }
}

/**
 * Exchanges an authorization code for tokens via the backend BFF endpoint.
 * The backend uses the client secret to perform the actual exchange with EntraID.
 * Sends the PKCE code_verifier for cross-origin validation.
 */
export async function exchangeCodeForTokens(code: string): Promise<void> {
  const codeVerifier = sessionStorage.getItem(VERIFIER_KEY) ?? '';
  sessionStorage.removeItem(VERIFIER_KEY);

  const response = await fetch(`${config.apiBaseUrl}/auth/callback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include', // Include cookies for refresh token
    body: JSON.stringify({
      code,
      redirectUri: config.entraIdRedirectUri,
      codeVerifier,
    }),
  });

  if (!response.ok) {
    throw new Error('Token exchange failed');
  }

  const data: BffTokenResponse = await response.json();
  storeTokens(data);
}

/**
 * Attempts a silent token refresh via the backend BFF endpoint.
 * The backend reads the HttpOnly refresh token cookie automatically.
 * Returns true if refresh succeeded, false otherwise.
 */
export async function silentRefresh(): Promise<boolean> {
  try {
    const response = await fetch(`${config.apiBaseUrl}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // Send HttpOnly cookie
    });

    if (!response.ok) {
      clearTokens();
      return false;
    }

    const data: BffTokenResponse = await response.json();
    storeTokens(data);
    return true;
  } catch {
    clearTokens();
    return false;
  }
}

/**
 * Initiates the EntraID login flow by redirecting to the authorize endpoint.
 * Generates a state value for CSRF protection, a PKCE code verifier/challenge,
 * and saves the return URL.
 */
export async function login(): Promise<void> {
  const state = generateState();
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(VERIFIER_KEY, codeVerifier);
  sessionStorage.setItem('ddns_admin_return_url', window.location.pathname);
  window.location.href = getAuthorizeUrl(state, codeChallenge);
}

/**
 * Validates the state parameter returned from EntraID against the stored value.
 * Returns true if valid, false if mismatched (potential CSRF).
 */
export function validateState(returnedState: string): boolean {
  const savedState = sessionStorage.getItem(STATE_KEY);
  sessionStorage.removeItem(STATE_KEY);
  return savedState === returnedState;
}

/**
 * Logs out the user by calling the backend logout endpoint,
 * then redirecting to EntraID's logout URL.
 */
export async function logout(): Promise<void> {
  try {
    const response = await fetch(`${config.apiBaseUrl}/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    clearTokens();

    if (response.ok) {
      const data: BffLogoutResponse = await response.json();
      window.location.href = data.logoutUrl;
    } else {
      // Fallback: redirect to EntraID logout directly
      const params = new URLSearchParams({
        post_logout_redirect_uri: window.location.origin,
      });
      window.location.href = `https://login.microsoftonline.com/${config.entraIdTenantId}/oauth2/v2.0/logout?${params.toString()}`;
    }
  } catch {
    clearTokens();
    const params = new URLSearchParams({
      post_logout_redirect_uri: window.location.origin,
    });
    window.location.href = `https://login.microsoftonline.com/${config.entraIdTenantId}/oauth2/v2.0/logout?${params.toString()}`;
  }
}

/**
 * Returns the current ID token for API authentication.
 */
export function getIdToken(): string | null {
  if (!tokens.idToken || !tokens.expiresAt) return null;
  if (Date.now() >= tokens.expiresAt) return null;
  return tokens.idToken;
}

/**
 * Returns the current access token.
 */
export function getAccessToken(): string | null {
  if (!tokens.accessToken || !tokens.expiresAt) return null;
  if (Date.now() >= tokens.expiresAt) return null;
  return tokens.accessToken;
}

/**
 * Returns the decoded ID token claims (role, name, email, etc.).
 */
export function getTokenClaims(): IdTokenClaims | null {
  return cachedClaims;
}

/**
 * Checks whether the user currently has valid tokens.
 */
export function isAuthenticated(): boolean {
  return getIdToken() !== null;
}

/**
 * Checks if the session has exceeded the 30-minute inactivity timeout.
 * Updates the last activity timestamp on each check.
 */
export function checkSessionTimeout(): boolean {
  const lastActivity = sessionStorage.getItem(SESSION_ACTIVITY_KEY);
  if (lastActivity) {
    const elapsed = Date.now() - parseInt(lastActivity, 10);
    if (elapsed > config.sessionTimeoutMs) {
      clearTokens();
      return true;
    }
  }
  updateLastActivity();
  return false;
}

/**
 * Updates the last activity timestamp to now.
 */
export function updateLastActivity(): void {
  sessionStorage.setItem(SESSION_ACTIVITY_KEY, Date.now().toString());
}

/**
 * Returns the saved return URL after login callback, then clears it.
 */
export function getReturnUrl(): string {
  const url = sessionStorage.getItem('ddns_admin_return_url') ?? '/';
  sessionStorage.removeItem('ddns_admin_return_url');
  return url;
}

/**
 * Checks if a refresh is possible (the backend cookie may exist even if we can't see it).
 * In the BFF pattern, we always attempt a refresh since the HttpOnly cookie is opaque to JS.
 */
export function hasRefreshToken(): boolean {
  // With HttpOnly cookies, we can't directly check — always try a refresh
  return true;
}

// ─── Internal Helpers ────────────────────────────────────────────────────────

function storeTokens(data: BffTokenResponse): void {
  tokens.accessToken = data.accessToken;
  tokens.idToken = data.idToken;
  tokens.expiresAt = Date.now() + data.expiresIn * 1000;

  // Decode and cache claims for role-based UI
  cachedClaims = decodeJwtPayload(data.idToken);

  updateLastActivity();
}

function clearTokens(): void {
  tokens.accessToken = null;
  tokens.idToken = null;
  tokens.expiresAt = null;
  cachedClaims = null;
  sessionStorage.removeItem(SESSION_ACTIVITY_KEY);
}
